import { useMemo, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { ClipboardCheck, AlertTriangle, Download } from "lucide-react";

const OPEN_CAPA = (s) => !["CLOSED", "VERIFIED", "COMPLETED", "DONE"].includes(String(s || "").toUpperCase());
const isPass = (a) => String(a.status || "").toUpperCase() === "PASSED" || Number(a.overallScore || 0) >= (a.passScore || 80);

function Stat({ label, value, tone = "text-stone-900" }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function AuditReports({ mode = "quality" }) {
  const { data: auditsRaw } = useResource("/audits");
  const { data: capaRaw } = useResource("/capa");
  const audits = Array.isArray(auditsRaw) ? auditsRaw : [];
  const capa = Array.isArray(capaRaw) ? capaRaw : [];

  const [f, setF] = useState({ from: "", to: "", type: "", status: "", person: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const auditsF = useMemo(() => audits.filter((a) => {
    const d = (a.dateTime || a.createdAt || "").slice(0, 10);
    if (f.from && d < f.from) return false;
    if (f.to && d > f.to) return false;
    if (f.type && String(a.auditType || "").toUpperCase() !== f.type.toUpperCase()) return false;
    if (f.status && String(a.status || "").toUpperCase() !== f.status.toUpperCase()) return false;
    if (f.person && !String(a.auditorName || "").toLowerCase().includes(f.person.toLowerCase())) return false;
    return true;
  }), [audits, f]);

  const capaF = useMemo(() => capa.filter((c) => {
    const d = (c.createdAt || c.dueDate || "").slice(0, 10);
    if (f.from && d < f.from) return false;
    if (f.to && d > f.to) return false;
    if (f.status && String(c.status || "").toUpperCase() !== f.status.toUpperCase()) return false;
    if (f.person && !String(c.assignedTo || "").toLowerCase().includes(f.person.toLowerCase())) return false;
    return true;
  }), [capa, f]);

  const today = new Date().toISOString().slice(0, 10);
  const openCapa = capaF.filter((c) => OPEN_CAPA(c.status));
  const overdue = openCapa.filter((c) => c.dueDate && c.dueDate < today);
  const openNc = capaF.filter((c) => String(c.type || "").toUpperCase() === "NC" && OPEN_CAPA(c.status));

  const exportCsv = () => {
    const rows = mode === "quality"
      ? [["Date", "Unit", "Type", "Auditor", "Status", "Score", "Critical", "Failed"],
         ...auditsF.map((a) => [(a.dateTime || "").slice(0, 10), a.bakeryName, a.auditType, a.auditorName, a.status, a.overallScore, a.criticalCount, a.failedCount])]
      : [["CAPA/NC", "Title", "Type", "Owner", "Priority", "Status", "Due", "Closed"],
         ...capaF.map((c) => [c.capaId, c.title, c.type || "CAPA", c.assignedTo, c.priority, c.status, c.dueDate, c.closedAt || c.closureDate || ""])];
    const csv = rows.map((r) => r.map((x) => `"${x ?? ""}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${mode}-audit-report.csv`; a.click(); window.URL.revokeObjectURL(url);
  };

  const Filters = (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      <div><label className="text-[10px] uppercase text-stone-500">From</label><Input type="date" data-testid="ar-from" value={f.from} onChange={(e) => set("from", e.target.value)} className="h-8" /></div>
      <div><label className="text-[10px] uppercase text-stone-500">To</label><Input type="date" data-testid="ar-to" value={f.to} onChange={(e) => set("to", e.target.value)} className="h-8" /></div>
      {mode === "quality" && <div><label className="text-[10px] uppercase text-stone-500">Audit Type</label><Input data-testid="ar-type" value={f.type} onChange={(e) => set("type", e.target.value)} placeholder="FSSAI / Hygiene…" className="h-8" /></div>}
      <div><label className="text-[10px] uppercase text-stone-500">Status</label><Input data-testid="ar-status" value={f.status} onChange={(e) => set("status", e.target.value)} className="h-8" /></div>
      <div><label className="text-[10px] uppercase text-stone-500">{mode === "quality" ? "Responsible" : "Owner"}</label><Input data-testid="ar-person" value={f.person} onChange={(e) => set("person", e.target.value)} className="h-8" /></div>
    </div>
  );

  if (mode === "action") {
    return (
      <div className="space-y-5">
        <PageHeader title="Audit & Compliance Action Report" subtitle="Non-conformities & CAPA — owners, due dates, closure and verification" />
        <div className="flex items-start justify-between gap-3">{Filters}<Button size="sm" variant="outline" className="gap-1.5" data-testid="ar-export" onClick={exportCsv}><Download className="h-3.5 w-3.5" /> Export</Button></div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Open Actions" value={openCapa.length} tone="text-amber-700" />
          <Stat label="Open Non-Conformities" value={openNc.length} tone="text-orange-700" />
          <Stat label="Overdue" value={overdue.length} tone="text-rose-600" />
          <Stat label="Total Tracked" value={capaF.length} />
        </div>
        <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="ar-action-table">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-[10px] uppercase text-stone-500"><tr>{["CAPA/NC", "Title", "Type", "Owner", "Priority", "Status", "Due", "Closure", "Verification"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-stone-100">
              {capaF.length === 0 ? <tr><td colSpan={9} className="px-3 py-6 text-center text-stone-400">No records.</td></tr> :
                capaF.map((c) => (
                  <tr key={c.id || c.capaId} className={`hover:bg-stone-50/60 ${c.dueDate && c.dueDate < today && OPEN_CAPA(c.status) ? "bg-rose-50/40" : ""}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.capaId}</td><td className="px-3 py-2">{c.title}</td>
                    <td className="px-3 py-2 text-xs">{c.type || "CAPA"}</td><td className="px-3 py-2 text-xs">{c.assignedTo || "—"}</td>
                    <td className="px-3 py-2 text-xs">{c.priority || "—"}</td><td className="px-3 py-2 text-xs font-semibold">{c.status}</td>
                    <td className="px-3 py-2 text-xs">{c.dueDate ? fmtDate(c.dueDate) : "—"}</td>
                    <td className="px-3 py-2 text-xs">{c.closedAt || c.closureDate ? fmtDate(c.closedAt || c.closureDate) : "—"}</td>
                    <td className="px-3 py-2 text-xs">{c.verifiedBy || c.verification || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Quality & Compliance Audit Report" subtitle="QA, FSSAI, Hygiene, Temperature, Production & Maintenance audits — consolidated" />
      <div className="flex items-start justify-between gap-3">{Filters}<Button size="sm" variant="outline" className="gap-1.5" data-testid="ar-export" onClick={exportCsv}><Download className="h-3.5 w-3.5" /> Export</Button></div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        <Stat label="Total Audits" value={auditsF.length} />
        <Stat label="Passed" value={auditsF.filter(isPass).length} tone="text-emerald-700" />
        <Stat label="Failed" value={auditsF.filter((a) => !isPass(a)).length} tone="text-rose-600" />
        <Stat label="Open NC" value={openNc.length} tone="text-orange-700" />
        <Stat label="Open CAPA" value={openCapa.length} tone="text-amber-700" />
        <Stat label="Overdue CAPA" value={overdue.length} tone="text-rose-600" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="ar-quality-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-[10px] uppercase text-stone-500"><tr>{["Date", "Unit", "Type", "Auditor", "Status", "Score", "Critical", "Failed"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone-100">
            {auditsF.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-stone-400">No audits match.</td></tr> :
              auditsF.map((a) => (
                <tr key={a.id} className="hover:bg-stone-50/60">
                  <td className="px-3 py-2 text-xs">{a.dateTime ? fmtDate(a.dateTime) : "—"}</td><td className="px-3 py-2">{a.bakeryName || "—"}</td>
                  <td className="px-3 py-2 text-xs">{a.auditType}</td><td className="px-3 py-2 text-xs">{a.auditorName || "—"}</td>
                  <td className="px-3 py-2 text-xs font-semibold">{a.status}</td><td className="px-3 py-2 text-xs">{a.overallScore != null ? `${a.overallScore}%` : "—"}</td>
                  <td className="px-3 py-2 text-xs text-rose-600">{a.criticalCount || 0}</td><td className="px-3 py-2 text-xs">{a.failedCount || 0}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
